import { Tooltip } from '@material-ui/core';
import { graphql, useStaticQuery } from 'gatsby';
import { orderBy, partition } from 'lodash-es';
import React from 'react';
import { ArrowUpRight, MapPin } from 'lucide-react';
import styled from '../styled';
import { COLORS } from '../themes/color';
import { BlockContent, CloudinaryImage } from '../types/Article';
import { Author } from '../types/Author';
import { Headline } from './Headline';
import { Kicker } from './Kicker';
import { Reveal } from './motion';

const INK = '#0e1c2b';

const SectionWrapper = styled('section')`
  padding: 100px 24px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;

  @media (max-width: 960px) {
    padding: 80px 24px;
  }
`;

const Description = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 16px auto 0;
  max-width: 640px;
`;

type IEvent = {
  title: string;
  startDate: string;
  endDate: string;
  slug: {
    current: string;
  };
  attendees: Author[];
  mainImage: CloudinaryImage;
  body: BlockContent;
  url: string;
  description: string;
  city: string;
};

function formatDates(startDate: string, endDate: string) {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const start = new Date(startDate);
  const end = new Date(endDate);
  const startMonth = months[start.getMonth()];
  const startDay = start.getDate();

  if (!endDate) {
    return `${startMonth} ${startDay}`;
  }

  const endMonth = months[end.getMonth()];
  const endDay = end.getDate();

  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    startDay === endDay
  ) {
    return `${startMonth} ${startDay}`;
  }

  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${startDay}–${endDay}`;
  } else {
    return `${startMonth} ${startDay}–${endMonth} ${endDay}`;
  }
}

const CardRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-top: 48px;
`;

const EventCard = styled('article')`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  width: 372px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 24px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #d4e0ff;
    box-shadow: 0 8px 28px rgba(46, 92, 255, 0.1);
  }
`;

const DateBadge = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 100px;
  background: #eef3ff;
  color: ${COLORS.PRIMARY_DARK};
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const EventTitleLink = styled('a')`
  color: ${INK};
  text-decoration: none;

  /* Stretch the click target across the whole card */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  h3 {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    line-height: 1.35;
  }

  svg {
    flex-shrink: 0;
    color: #94a3b8;
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: ${COLORS.PRIMARY};
  }
`;

const EventMeta = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: #64748b;

  svg {
    color: #94a3b8;
  }
`;

const EventDescription = styled('p')`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #64748b;
`;

const MeetRow = styled('div')`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
  width: 100%;
`;

const MeetLabel = styled('div')`
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
`;

const AttendeeStack = styled('div')`
  display: flex;
`;

const AttendeeWrapper = styled('div')`
  transition: transform 0.15s ease-in-out;

  &:not(:first-child) {
    margin-left: -8px;
  }

  &:hover {
    transform: scale(1.12);
    z-index: 1;
  }
`;

const AttendeeImage = styled('img')`
  display: block;
  width: 34px;
  height: 34px;
  border-radius: 100%;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(14, 28, 43, 0.12);
`;

const Attendee = ({ attendee }: { attendee: Author }) => {
  const { name, image, linkedin } = attendee;
  const matches = new RegExp(/v[0-9]+\/(.+)/).exec(image.image.src);
  if (!matches) {
    return null;
  }
  const src = matches[1];
  const ASSET_URL = 'https://assets.affilimate.com';
  const toSize = (size: number, src: string) => {
    return `${ASSET_URL}/image/upload/w_${size}/${src}`;
  };

  const fullSrc = `${ASSET_URL}/${src}`;
  const webPSrc = src.replace('.png', '.webp').replace('.jpg', '.webp');
  const jpgSrc = src.replace('.png', '.jpg');

  return (
    <Tooltip title={`Meet with ${name}`} placement="top">
      <AttendeeWrapper>
        <a href={linkedin} target="_blank" rel="noopener noreferrer">
          <picture>
            <source
              type="image/webp"
              srcSet={`${toSize(170, webPSrc)} 170w, ${toSize(
                85,
                webPSrc
              )} 85w`}
              sizes="100wv"
            />
            <source
              type="image/jpeg"
              srcSet={`${toSize(170, jpgSrc)} 170w, ${toSize(85, jpgSrc)} 85w`}
              sizes="100wv"
            />
            <AttendeeImage
              src={fullSrc}
              alt={name}
              loading="lazy"
              width="34"
              height="34"
            />
          </picture>
        </a>
      </AttendeeWrapper>
    </Tooltip>
  );
};

const nowInMs = Date.now().valueOf();

const firstNames = (attendees: Author[]) => {
  const names = attendees.map((a) => a.name.split(' ')[0]);
  if (names.length <= 1) {
    return names.join('');
  }
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

const UpcomingEvent = ({ event }: { event: IEvent }) => {
  const { title, startDate, endDate, city, url, attendees, description } =
    event;

  return (
    <EventCard>
      <DateBadge>{formatDates(startDate, endDate)}</DateBadge>
      <EventTitleLink href={url} target="_blank" rel="noopener noreferrer">
        <h3>
          {title} <ArrowUpRight size={16} />
        </h3>
      </EventTitleLink>
      <EventMeta>
        <MapPin size={14} /> {city}
      </EventMeta>
      {description && <EventDescription>{description}</EventDescription>}
      {attendees && attendees.length > 0 && (
        <MeetRow>
          <AttendeeStack>
            {attendees.map((a) => (
              <Attendee key={a.name} attendee={a} />
            ))}
          </AttendeeStack>
          <MeetLabel>Meet {firstNames(attendees)} there</MeetLabel>
        </MeetRow>
      )}
    </EventCard>
  );
};

const PastEventsLabel = styled('div')`
  margin-top: 56px;
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const PastEventsRow = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
  max-width: 960px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PastEventChip = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  font-size: 13px;
  color: #64748b;
  min-width: 0;

  strong {
    color: #475569;
    font-weight: 600;
    line-height: 1.35;
  }

  span {
    color: #94a3b8;
    line-height: 1.4;
  }
`;

const EventList = () => {
  const data = useStaticQuery(graphql`
    query {
      allSanityEvent {
        edges {
          node {
            slug {
              current
            }
            title
            url
            description
            startDate
            endDate
            city
            attendees {
              name
              jobTitle
              linkedin
              slug {
                current
              }
              image {
                alt
                image {
                  width
                  height
                  src
                }
              }
            }
          }
        }
      }
    }
  `);

  const [upcomingEvents, pastEvents]: IEvent[][] = partition(
    data.allSanityEvent.edges.map((edge: any) => edge.node),
    (event: IEvent) => {
      const startDate = new Date(event.startDate).valueOf();
      return startDate > nowInMs;
    }
  );

  return (
    <SectionWrapper>
      <Reveal>
        <Kicker style={{ marginBottom: 0 }}>On the road</Kicker>
        <Headline component="h2" style={{ marginTop: 16 }}>
          Meet us at industry events
        </Headline>
        <Description>
          We travel to the industry&apos;s biggest events year-round. Come talk
          partnerships, product, and where commerce is heading.
        </Description>
      </Reveal>
      <CardRow>
        {orderBy(upcomingEvents, (e) => new Date(e.startDate).valueOf()).map(
          (e, index) => (
            <Reveal key={e.slug.current} delay={index * 80}>
              <UpcomingEvent event={e} />
            </Reveal>
          )
        )}
      </CardRow>
      {pastEvents.length > 0 && (
        <Reveal delay={120}>
          <PastEventsLabel>Where we&apos;ve been</PastEventsLabel>
          <PastEventsRow>
            {orderBy(pastEvents, (e) => -new Date(e.startDate).valueOf())
              .slice(0, 3)
              .map((e) => (
                <PastEventChip key={e.slug.current}>
                  <strong>{e.title}</strong>
                  <span>
                    {e.city} · {formatDates(e.startDate, e.endDate)}
                  </span>
                </PastEventChip>
              ))}
          </PastEventsRow>
        </Reveal>
      )}
    </SectionWrapper>
  );
};

export default EventList;
